// ProjectPage - полная версия со всеми модалками (из orig/main.py)

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsApi, scenesApi, getFullUrlWithTimestamp } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useTaskPolling } from '../hooks/useTaskPolling';
import type { Project } from '../types';
import TaskProgressModal from '../components/Modals/TaskProgressModal';
import ViewResourceModal from '../components/Modals/ViewResourceModal';
import VideoModal from '../components/Modals/VideoModal';
import LipsyncModal from '../components/Modals/LipsyncModal';
import FirstFrameModal from '../components/Modals/FirstFrameModal';
import './ProjectPage.css';

type TabType = 'scenes' | 'scenario';

const ProjectPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { task, progress, isPolling, startPolling, stopPolling } = useTaskPolling();

  const [project, setProject] = useState<Project | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('scenes');
  const [loading, setLoading] = useState(true);

  // Modal states
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskName, setTaskName] = useState('');
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewResourceUrl, setViewResourceUrl] = useState<string | null>(null);
  const [viewResourceTitle, setViewResourceTitle] = useState('');
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [lipsyncModalOpen, setLipsyncModalOpen] = useState(false);
  const [firstFrameModalOpen, setFirstFrameModalOpen] = useState(false);
  const [currentSceneNumber, setCurrentSceneNumber] = useState<number>(0);
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [currentSceneJson, setCurrentSceneJson] = useState<any>(null);

  useEffect(() => {
    if (projectId) {
      loadProject();
    }
  }, [projectId]);

  const loadProject = async () => {
    try {
      const data = await projectsApi.get(projectId!);
      setProject(data);
    } catch (error) {
      console.error('Failed to load project:', error);
      alert('Error loading project');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleTaskCreated = (taskId: string, name: string) => {
    setTaskName(name);
    setTaskModalOpen(true);
    startPolling(taskId);
  };

  const handleTaskComplete = async () => {
    stopPolling();
    setTaskModalOpen(false);
    // Небольшая задержка, чтобы бэкенд успел обновить scenario.json
    await new Promise(resolve => setTimeout(resolve, 500));
    loadProject();
  };

  const handleViewResource = (url: string, title: string) => {
    setViewResourceUrl(url);
    setViewResourceTitle(title);
    setViewModalOpen(true);
  };

  const handleEditScene = async (sceneNumber: number) => {
    const scene = project?.scenes?.find(s => s.scene_number === sceneNumber);
    if (!scene) return;

    const newPrompt = prompt('Edit scene prompt:', scene.prompt);
    if (newPrompt && newPrompt !== scene.prompt) {
      try {
        await scenesApi.edit(projectId!, sceneNumber, newPrompt);
        loadProject();
      } catch (error) {
        console.error('Failed to edit scene:', error);
        alert('Error editing scene');
      }
    }
  };

  const handleResetScene = async (sceneNumber: number) => {
    if (!window.confirm(`Reset Scene ${sceneNumber}? This will delete all generated content.`)) return;

    try {
      await scenesApi.reset(projectId!, sceneNumber);
      loadProject();
    } catch (error) {
      console.error('Failed to reset scene:', error);
      alert('Error resetting scene');
    }
  };

  const handleDeleteScene = async (sceneNumber: number) => {
    if (!window.confirm(`Delete Scene ${sceneNumber}? This cannot be undone.`)) return;

    try {
      await scenesApi.deleteCustom(projectId!, sceneNumber);
      loadProject();
    } catch (error) {
      console.error('Failed to delete scene:', error);
      alert('Error deleting scene');
    }
  };

  if (loading) {
    return <div className="loading-spinner"></div>;
  }

  if (!project) {
    return <div>Project not found</div>;
  }

  return (
    <div className="container">
      <button onClick={() => navigate('/')} className="btn btn-secondary" style={{ marginBottom: '20px' }}>
        ← Back to Projects
      </button>

      <div className="header">
        <div className="header-left">
          <h1>{project.name}</h1>
        </div>
        <div className="header-right">
          <div className="user-info">{user?.username}</div>
        </div>
      </div>

      <div className="nav-tabs">
        <button className={`nav-tab ${activeTab === 'scenes' ? 'active' : ''}`} onClick={() => setActiveTab('scenes')}>
          Scenes
        </button>
        <button className={`nav-tab ${activeTab === 'scenario' ? 'active' : ''}`} onClick={() => setActiveTab('scenario')}>
          Scenario
        </button>
      </div>

      {activeTab === 'scenes' && (
        <div>
          {!project.scenes || project.scenes.length === 0 ? (
            <p>No scenes found</p>
          ) : (
            <div className="scenes-horizontal-scroll">
              {project.scenes.map((scene) => {
                const hasVideo = scene.video_url;
                const hasImage = scene.image_url;
                const firstFrameUrl = scene.first_frame_url || scene.image_url;
                const hasFirstFrame = !!firstFrameUrl;

                return (
                  <div key={scene.scene_number} className="scene-card">
                    {/* Split preview: first frame left, video right */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                      {/* Left: First Frame */}
                      <div style={{ flex: 1, background: '#f8f9fa', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '150px', overflow: 'hidden' }}>
                        {hasFirstFrame ? (
                          <img src={getFullUrlWithTimestamp(firstFrameUrl)} alt={`Scene ${scene.scene_number} - First Frame`} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                        ) : (
                          <div style={{ textAlign: 'center', opacity: 0.4 }}>
                            <div style={{ fontSize: '32px' }}>🖼️</div>
                            <div style={{ fontSize: '10px', marginTop: '5px' }}>First Frame</div>
                          </div>
                        )}
                      </div>

                      {/* Right: Video */}
                      <div style={{ flex: 1, background: '#f8f9fa', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '150px', overflow: 'hidden' }}>
                        {hasVideo ? (
                          <video src={getFullUrlWithTimestamp(scene.video_url)} controls style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                        ) : (
                          <div style={{ textAlign: 'center', opacity: 0.4 }}>
                            <div style={{ fontSize: '32px' }}>🎬</div>
                            <div style={{ fontSize: '10px', marginTop: '5px' }}>Video</div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="scene-info">
                      <strong>Scene {scene.scene_number}</strong>
                      {scene.is_custom && (
                        <span style={{ background: '#667eea', color: 'white', padding: '2px 6px', borderRadius: '3px', fontSize: '10px', marginLeft: '5px' }}>
                          CUSTOM
                        </span>
                      )}
                    </div>
                    <div className="scene-actions">
                      <button
                        onClick={() => {
                          setCurrentSceneJson(scene);
                          setJsonModalOpen(true);
                        }}
                        className="btn btn-sm btn-secondary"
                      >
                        📄 JSON
                      </button>
                      <button
                        onClick={() => {
                          setCurrentSceneNumber(scene.scene_number);
                          setFirstFrameModalOpen(true);
                        }}
                        className="btn btn-sm btn-primary"
                      >
                        🎬 First Frame
                      </button>
                      <button
                        onClick={() => {
                          setCurrentSceneNumber(scene.scene_number);
                          setVideoModalOpen(true);
                        }}
                        className="btn btn-sm btn-success"
                      >
                        Video
                      </button>
                      <button
                        onClick={() => {
                          setCurrentSceneNumber(scene.scene_number);
                          setLipsyncModalOpen(true);
                        }}
                        className="btn btn-sm btn-info"
                        style={{ background: '#9b59b6' }}
                      >
                        LipSync
                      </button>
                      <button onClick={() => handleResetScene(scene.scene_number)} className="btn btn-sm btn-danger">
                        Reset
                      </button>
                      {scene.is_custom && (
                        <button onClick={() => handleDeleteScene(scene.scene_number)} className="btn btn-sm btn-danger">
                          🗑️ Delete
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'scenario' && (
        <div>
          {!project.scenes || project.scenes.length === 0 ? (
            <p>No scenes found</p>
          ) : (
            project.scenes.map((scene) => (
              <div key={scene.scene_number} className="scenario-card">
                <h4>Scene {scene.scene_number}</h4>
                <div className="scenario-field">
                  <span className="scenario-field-label">Timestamp:</span>
                  <div className="scenario-field-value compact">
                    {scene.timestamp_start || 0}s - {scene.timestamp_end || 5}s
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Modals */}
      <TaskProgressModal
        isOpen={taskModalOpen}
        taskName={taskName}
        progress={progress}
        task={task}
        onClose={() => setTaskModalOpen(false)}
        onAccept={handleTaskComplete}
      />

      <ViewResourceModal
        isOpen={viewModalOpen}
        title={viewResourceTitle}
        resourceUrl={viewResourceUrl}
        onClose={() => setViewModalOpen(false)}
      />

      <VideoModal
        isOpen={videoModalOpen}
        projectId={projectId!}
        sceneNumber={currentSceneNumber}
        firstFrameUrl={`/api/static/projects/${projectId}/first_frames/scene_${currentSceneNumber}_frame.jpg`}
        scenePrompt={project.scenes?.find(s => s.scene_number === currentSceneNumber)?.video_prompt}
        onClose={() => setVideoModalOpen(false)}
        onTaskCreated={(taskId) => handleTaskCreated(taskId, `Video Generation - Scene ${currentSceneNumber}`)}
      />

      <LipsyncModal
        isOpen={lipsyncModalOpen}
        projectId={projectId!}
        sceneNumber={currentSceneNumber}
        onClose={() => setLipsyncModalOpen(false)}
        onTaskCreated={(taskId) => handleTaskCreated(taskId, `LipSync Generation - Scene ${currentSceneNumber}`)}
      />

      <FirstFrameModal
        isOpen={firstFrameModalOpen}
        projectId={projectId!}
        sceneNumber={currentSceneNumber}
        sceneData={project.scenes?.find(s => s.scene_number === currentSceneNumber)}
        onClose={() => setFirstFrameModalOpen(false)}
        onTaskCreated={(taskId) => handleTaskCreated(taskId, `First Frame Generation - Scene ${currentSceneNumber}`)}
      />

      {/* JSON Modal */}
      {jsonModalOpen && currentSceneJson && (() => {
        // Функция для преобразования всех URL в полные
        const convertUrlsToFull = (obj: any): any => {
          if (obj === null || obj === undefined) return obj;

          if (Array.isArray(obj)) {
            return obj.map(item => convertUrlsToFull(item));
          }

          if (typeof obj === 'object') {
            const converted: any = {};
            for (const [key, value] of Object.entries(obj)) {
              // Если ключ заканчивается на _url или содержит url, преобразуем значение
              if ((key.endsWith('_url') || key.includes('url')) && typeof value === 'string') {
                converted[key] = getFullUrlWithTimestamp(value);
              } else if (typeof value === 'object') {
                converted[key] = convertUrlsToFull(value);
              } else {
                converted[key] = value;
              }
            }
            return converted;
          }

          return obj;
        };

        const sceneWithFullUrls = convertUrlsToFull(currentSceneJson);

        return (
          <div className="modal-overlay" onClick={() => setJsonModalOpen(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '80vh' }}>
              <div className="modal-header">
                <h3>Scene {currentSceneJson.scene_number} - JSON</h3>
                <button className="modal-close" onClick={() => setJsonModalOpen(false)}>×</button>
              </div>
              <div className="modal-body" style={{ maxHeight: '60vh', overflow: 'auto' }}>
                <pre style={{
                  background: '#f5f5f5',
                  padding: '15px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  lineHeight: '1.5',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {JSON.stringify(sceneWithFullUrls, null, 2)}
                </pre>
              </div>
              <div className="modal-actions" style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '15px' }}>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(sceneWithFullUrls, null, 2));
                    alert('JSON copied to clipboard!');
                  }}
                  className="btn btn-primary"
                >
                  📋 Copy JSON
                </button>
                <button onClick={() => setJsonModalOpen(false)} className="btn btn-secondary">
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ProjectPage;
